import {
  getPerpetualDexContractsFromDeployment,
  hasDeployment,
} from "../../../deployments";
import { ChainIdEnum } from "../../../enums/chain";
import { getTokenAddress as getListedTokenAddress } from "../../tokens";
import {
  AppBranding,
  ERC20Addresses,
  PerpetualDexContracts,
  TokenDecimals,
} from "../types";

// Default RPC API Key from environment
export const RPC_API_KEY = process.env.REACT_APP_RPC_KEY || "";
export const RPC_WSS_URL =
  process.env.REACT_APP_RPC_WSS_URL || process.env.REACT_APP_RPC_WS_URL || "";
// Relayer configuration lives in src/config/relayers — one place, shared with
// src/setupProxy.js, with no per-chain hardcoded URL. A wrong relayer is
// indistinguishable from a right one at runtime and gets orders rejected as
// invalid signatures, so it is resolved and validated there rather than here.

/**
 * The RPC URL(s) for a chain that has no public endpoint. Keyed RPC URLs are
 * secrets and never live in source: the build supplies REACT_APP_RPC_URL. Unset
 * means NO RPC (an empty list), which `assertChainRpcConfigured` rejects at boot
 * for the active chain instead of silently shipping someone's key.
 */
export const envRpcUrls = (): string[] => {
  const url = (process.env.REACT_APP_RPC_URL || "").trim();
  return url ? [url] : [];
};

/**
 * Derive the WebSocket endpoint from an HTTP RPC URL: swap the scheme, and a
 * trailing `/rpc` path segment becomes `/ws` (the convention of the
 * self-hosted RPC proxies). Query strings are kept as they are.
 */
export const toRpcWebSocketUrl = (rpcUrl?: string): string | undefined => {
  if (!rpcUrl) return undefined;
  const match = /^(https?):\/\/([^?#]*)(.*)$/.exec(rpcUrl);
  if (!match) return undefined;
  const [, scheme, hostAndPath, rest] = match;
  const path = hostAndPath.replace(/\/rpc\/?$/, "/ws");
  return `${scheme === "https" ? "wss" : "ws"}://${path}${rest}`;
};

/**
 * Get perpetual DEX contracts from deployment JSON if available,
 * otherwise return fallback hardcoded values
 */
// Chain the app is actually running. Read REACT_APP_CHAIN_ID directly (avoids a circular import with
// ./index.ts). A missing/empty deployment for THIS chain is fatal at runtime — see getPerpetualDexConfig.
const ACTIVE_CHAIN_ID = Number(process.env.REACT_APP_CHAIN_ID) || 0;

const EMPTY_PERP_CONTRACTS: Omit<PerpetualDexContracts, "usdc"> = {
  router: "",
  crossMargin: "",
  orderHandler: "",
  orderVault: "",
  marketFactory: "",
  unifiedVault: "",
  unifiedMarginAdapter: "",
  positionHandler: "",
  backstopVault: "",
  oracle: "",
  dataStore: "",
  marketHandler: "",
  batchOrderManager: "",
  balanceManager: "",
  perpRouter: "",
  markets: {},
};

export const getPerpetualDexConfig = (
  chainId: ChainIdEnum,
  fallback: PerpetualDexContracts
): PerpetualDexContracts => {
  const fromDeployment = hasDeployment(chainId)
    ? getPerpetualDexContractsFromDeployment(chainId)
    : undefined;
  if (fromDeployment && fromDeployment.router) {
    const usdc = fromDeployment.usdc || getUsdcAddress(chainId, fallback.usdc);
    return { ...fromDeployment, usdc };
  }

  // No / empty deployment JSON. For the ACTIVE chain, fail LOUD at runtime (the app must not silently
  // trade on stale or empty contract addresses). Non-active chains get empty contracts (never used).
  const msg =
    `[ChainConfig] Missing deployed contract addresses for chain ${chainId}. ` +
    `Sync + rebuild src/deployments/${chainId}.json via the deploy orchestrator. ` +
    `Refusing stale hardcoded fallbacks.`;
  if (chainId === ACTIVE_CHAIN_ID) {
    throw new Error(msg);
  }
  console.error(msg);
  const usdc = getUsdcAddress(chainId, fallback.usdc);
  return { ...EMPTY_PERP_CONTRACTS, usdc };
};

/**
 * Get token addresses from the token registry. Contract addresses come from
 * deployments; ERC20 addresses live in config/tokens/tokens.json.
 */
export const getUsdcAddress = (chainId: ChainIdEnum, fallback: string): string => {
  return (
    getListedTokenAddress(chainId, "USDC") ||
    getListedTokenAddress(chainId, "MUSDC") ||
    fallback
  );
};

export const getTokenAddress = (
  chainId: ChainIdEnum,
  symbol: string,
  fallback: string
): string => {
  return getListedTokenAddress(chainId, symbol) || fallback;
};

// ============================================
// DEFAULT APP BRANDING CONFIGURATIONS
// ============================================

/**
 * MarginX branding (default for most chains)
 */
export const MARGINX_BRANDING: AppBranding = {
  appName: "MarginX Exchange",
  shortName: "MarginX",
  description:
    "High-performance decentralized exchange for spot and perpetual trading.",
  keywords: ["marginx", "defi", "perpetuals", "trading", "exchange", "crypto"],
  domain: "marginx.trade",
  themeColor: "#ffffff",
  backgroundColor: "#000000",
  logoFolder: "marginx",
  links: {
    home: "https://app.marginx.trade",
    docs: "https://docs.marginx.trade",
    twitter: "https://x.com/marginx_trade",
    discord: "https://discord.com/invite/marginx",
    github: "https://github.com/procyon-labs-io",
    telegram: "https://t.me/marginx_trade",
    termsOfService: "https://marginx.trade/terms",
  },
};

/**
 * MegaETH branding (for MegaETH testnet)
 */
export const MEGAETH_BRANDING: AppBranding = {
  appName: "MarginX Perps",
  shortName: "MarginX",
  description: "High-performance perpetual trading on MegaETH.",
  keywords: [
    "marginx",
    "megaeth",
    "perps",
    "perpetuals",
    "trading",
    "defi",
    "crypto",
  ],
  domain: "marginx.trade",
  themeColor: "#ffffff",
  backgroundColor: "#000000",
  logoFolder: "marginx",
  links: {
    home: "https://app.marginx.trade",
    docs: "https://docs.marginx.trade",
    twitter: "https://x.com/marginx_trade",
    discord: "https://discord.gg/marginx",
    github: "https://github.com/procyon-labs-io",
    telegram: "https://t.me/marginx_trade",
    termsOfService: "https://marginx.trade/terms",
  },
};

/**
 * Dogeos branding (for Dogeos testnet)
 */
export const DOGEOS_BRANDING: AppBranding = {
  appName: "MuchFi",
  shortName: "MuchFi",
  description: "High-performance perpetual trading on Dogeos.",
  keywords: [
    "dogeos",
    "doge",
    "muchfi",
    "perps",
    "perpetuals",
    "trading",
    "defi",
    "crypto",
  ],
  domain: "muchfi.xyz",
  themeColor: "#ffffff",
  backgroundColor: "#000000",
  logoFolder: "muchfi",
  links: {
    home: "https://unified-app.muchfi.xyz",
    docs: "https://docs.muchfi.com",
    twitter: "https://x.com/muchfi",
    discord: "https://discord.gg/muchfi",
    github: "https://github.com/muchfi",
    telegram: "https://t.me/muchfi",
    termsOfService: "https://muchfi.xyz/terms",
  },
};

/**
 * Monad branding (for Monad testnet)
 */
export const MONAD_BRANDING: AppBranding = {
  appName: "MarginX Exchange",
  shortName: "MarginX",
  description: "High-performance spot trading on Monad.",
  keywords: ["marginx", "monad", "spot", "trading", "defi", "crypto"],
  domain: "marginx.trade",
  themeColor: "#ffffff",
  backgroundColor: "#000000",
  logoFolder: "marginx",
  links: {
    home: "https://app.marginx.trade",
    docs: "https://docs.marginx.trade",
    twitter: "https://x.com/marginx_trade",
    discord: "https://discord.com/invite/marginx",
    github: "https://github.com/procyon-labs-io",
    telegram: "https://t.me/marginx_trade",
    termsOfService: "https://marginx.trade/terms",
  },
};

/**
 * RealTime Exchange branding (for Arc testnet)
 */
export const REALTIME_BRANDING: AppBranding = {
  appName: "RealTime Exchange",
  shortName: "RealTime",
  description: "High-performance decentralized exchange on Arc.",
  keywords: ["realtime", "arc", "defi", "perpetuals", "spot", "trading", "exchange", "crypto"],
  domain: "realtime.exchange",
  themeColor: "#ffffff",
  backgroundColor: "#000000",
  logoFolder: "realtime",
  links: {
    home: "https://arc-testnet.realtime.exchange",
    docs: "https://docs.realtime.exchange",
    apiReference: "https://docs.realtime.exchange/api-reference/introduction",
    twitter: "https://x.com/realtime_defi",
    discord: "https://discord.gg/realtime-exchange",
    github: "https://github.com/realtime-exchange",
    mediaKit: "https://docs.realtime.exchange/home/overview/brand-kit",
    telegram: "",
    termsOfService: "https://realtime.exchange/terms",
  },
};

// Default token decimals
export const DEFAULT_TOKEN_DECIMALS: TokenDecimals = {
  usdc: 6,
  usdt: 6,
  weth: 18,
  wbtc: 8,
  sdai: 6,
};

// Empty ERC20 addresses template
export const EMPTY_ERC20: ERC20Addresses = {
  usdc: "",
  "usdc-native": "",
  usdt: "",
  weth: "",
  wbtc: "",
  l2usdc: "",
  l2usdt: "",
  l2weth: "",
  l2wbtc: "",
  govToken: "",
};
